import { 
  Ticket, 
  TicketComment, 
  CreateTicketRequest, 
  UpdateTicketRequest, 
  CreateCommentRequest,
  UpdateCommentRequest,
  TicketStats,
  PaginatedTickets,
  TicketAttachment
} from '../types/ticket';

let API_BASE_URL: string;

API_BASE_URL = `${window.location.origin}`;
class TicketService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private getAuthHeaderOnly() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`
    };
  }

  async getTickets(params?: {
    page?: number;
    per_page?: number;
    status?: string;
    priority?: string;
    category?: string;
    assigned_to_id?: number;
    company_id?: number;
  }): Promise<PaginatedTickets> {
    const queryParams = new URLSearchParams();

    const perPage = params?.per_page ?? 10;
    const page = params?.page ?? 1;
    const limit = perPage;
    const offset = Math.max(0, (page - 1) * perPage);

    queryParams.append('limit', limit.toString());
    queryParams.append('offset', offset.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.assigned_to_id) queryParams.append('assigned_to_me', 'true');

    const response = await fetch(`${API_BASE_URL}/api/v2/tickets/?${queryParams}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Falha ao carregar tickets');
    }

    const data = await response.json();
    const total: number = data.total ?? 0;
    const usedLimit: number = data.limit ?? limit;
    const usedOffset: number = data.offset ?? offset;
    const currentPage = Math.floor(usedOffset / Math.max(usedLimit, 1)) + 1;
    const totalPages = Math.ceil(total / Math.max(usedLimit, 1));

    return {
      tickets: data.tickets ?? [],
      total,
      page: currentPage,
      per_page: usedLimit,
      total_pages: totalPages,
    };
  }

  async getTicket(id: number): Promise<Ticket> {
    const response = await fetch(`${API_BASE_URL}/api/v2/tickets/${id}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Falha ao carregar ticket');
    }

    return response.json();
  }

  async createTicket(ticket: CreateTicketRequest): Promise<Ticket> {
    const response = await fetch(`${API_BASE_URL}/api/v2/tickets/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(ticket),
    });

    if (!response.ok) {
      throw new Error('Falha ao criar ticket');
    }

    return response.json();
  }

  async updateTicket(id: number, ticket: UpdateTicketRequest): Promise<Ticket> {
    const response = await fetch(
      `${API_BASE_URL}/api/v2/tickets/${id}`,
      {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(ticket)
      }
    );

    if (!response.ok) {
      throw new Error('Falha ao atualizar ticket');
    }

    return response.json();
  }

  async getTicketComments(ticketId: number): Promise<TicketComment[]> {
    const response = await fetch(`${API_BASE_URL}/api/v2/tickets/${ticketId}/comments`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Falha ao carregar comentários');
    }

    return response.json();
  }

  async createComment(ticketId: number, comment: CreateCommentRequest): Promise<TicketComment> {
    const response = await fetch(`${API_BASE_URL}/api/v2/tickets/${ticketId}/comments`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(comment),
    });

    if (!response.ok) {
      throw new Error('Falha ao criar comentário');
    }

    return response.json();
  }

  async listAttachments(ticketId: number): Promise<TicketAttachment[]> {
    const response = await fetch(
      `${API_BASE_URL}/api/v2/tickets/${ticketId}/attachments`,
      {
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Falha ao carregar anexos');
    }
    return (await response.json()) as TicketAttachment[];
  }

  async uploadAttachments(ticketId: number, files: File[], commentId?: number): Promise<TicketAttachment[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (commentId) formData.append('comment_id', String(commentId));

    const response = await fetch(
      `${API_BASE_URL}/api/v2/tickets/${ticketId}/attachments`,
      {
        method: 'POST',
        headers: this.getAuthHeaderOnly(),
        body: formData
      }
    );
    if (!response.ok) {
      throw new Error('Falha ao enviar anexos');
    }
    return (await response.json()) as TicketAttachment[];
  }

  async updateComment(ticketId: number, commentId: number, comment: UpdateCommentRequest): Promise<TicketComment> {
    const response = await fetch(
      `${API_BASE_URL}/api/v2/tickets/${ticketId}/comments/${commentId}`,
      {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(comment)
      }
    );

    if (!response.ok) {
      throw new Error('Falha ao atualizar comentário');
    }

    return response.json();
  }

  async deleteComment(ticketId: number, commentId: number): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/api/v2/tickets/${ticketId}/comments/${commentId}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Falha ao excluir comentário');
    }
  }

  async deleteAttachment(ticketId: number, attachmentId: number): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/api/v2/tickets/${ticketId}/attachments/${attachmentId}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Falha ao excluir arquivo');
    }
  }

  /** Re-adicionado: excluir ticket */
  async deleteTicket(id: number): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/api/v2/tickets/${id}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Falha ao excluir ticket');
    }
  }

  /** Re-adicionado: estatísticas de tickets */
  async getTicketStats(companyId?: number): Promise<TicketStats> {
    const queryParams = new URLSearchParams();
    if (companyId) queryParams.append('company_id', companyId.toString());

    const response = await fetch(`${API_BASE_URL}/api/v2/tickets/stats?${queryParams}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Falha ao carregar estatísticas');
    }

    return response.json();
  }
}

export const ticketService = new TicketService();
